import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import Loader from '../components/Loader';

interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  priceFrom?: number;
  priceTo?: number;
  priceType: string;
  currency: string;
  rating: number;
  reviewsCount: number;
  user: {
    firstName: string;
    rating: number;
  };
}

export default function Services() {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [
    { id: 'cleaning', icon: '🧹', label: 'Клининг' },
    { id: 'construction', icon: '🔨', label: 'Стройка' },
    { id: 'it', icon: '💻', label: 'IT' },
    { id: 'design', icon: '🎨', label: 'Дизайн' },
    { id: 'delivery', icon: '🚚', label: 'Доставка' },
    { id: 'education', icon: '📚', label: 'Обучение' }
  ];

  useEffect(() => {
    loadServices();
  }, [selectedCategory]);

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await apiService.getServices(params);
      setServices(response.data.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (service: Service) => {
    const symbols: Record<string, string> = {
      RUB: '₽',
      KZT: '₸',
      BYN: 'Br'
    };
    const symbol = symbols[service.currency] || service.currency;

    if (service.priceType === 'negotiable') return 'Договорная';
    if (service.priceFrom && service.priceTo) {
      return `${service.priceFrom.toLocaleString()} - ${service.priceTo.toLocaleString()} ${symbol}`;
    }
    if (service.priceFrom) return `от ${service.priceFrom.toLocaleString()} ${symbol}`;
    return 'Договорная';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">💼 Услуги</h1>
        <Link to="/services/create" className="btn-primary text-sm">
          ➕ Создать
        </Link>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-full whitespace-nowrap text-sm ${
            !selectedCategory
              ? 'bg-tg-theme-button text-tg-theme-button-text'
              : 'bg-tg-theme-secondary-bg'
          }`}
        >
          Все
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm flex items-center gap-1 ${
              selectedCategory === cat.id
                ? 'bg-tg-theme-button text-tg-theme-button-text'
                : 'bg-tg-theme-secondary-bg'
            }`}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Services List */}
      {isLoading ? (
        <Loader className="py-8" />
      ) : services.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-4xl mb-2">📭</p>
          <p className="text-tg-theme-hint">Услуг пока нет</p>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <Link key={service.id} to={`/services/${service.id}`}>
              <div className="card hover:opacity-90">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold flex-1">{service.title}</h3>
                  <span className="text-tg-theme-button font-bold ml-2">
                    {formatPrice(service)}
                  </span>
                </div>
                <p className="text-sm text-tg-theme-hint line-clamp-2 mb-3">
                  {service.description}
                </p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-tg-theme-hint">
                    ⭐️ {service.rating.toFixed(1)} ({service.reviewsCount} отзывов)
                  </span>
                  <span className="text-tg-theme-hint">
                    👤 {service.user.firstName}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
